import { DictionaryValue, Option, VerifierCompanyAddressModel } from 'types/types';

export const PoorMansError = (errors: any): string => {
  //Server errors are inconsistent. Try parse response as errors

  if (typeof errors === 'string') {
    try {
      const object = JSON.parse(errors);
      return PoorMansError(object);
    } catch {
      return errors;
    }
  }

  if (!errors) {
    return '';
  }

  if (errors.detail) {
    return errors.detail;
  }

  if (errors.title) {
    return errors.title;
  }

  if (errors.errorMessage) {
    return errors.errorMessage;
  }

  if (errors.ErrorMessage) {
    return errors.ErrorMessage;
  }

  if (errors.response) {
    return PoorMansError(errors.response);
  }

  const getErrorFromArray = (errorsList: any): string => {
    if (errorsList.length > 0) {
      if (Array.isArray(errorsList[0])) {
        return PoorMansError(errorsList[0][0]);
      }

      return PoorMansError(errorsList[0]);
    }

    return '';
  };

  if (errors.Errors) {
    const errorsList = Object.values(errors.Errors);
    return getErrorFromArray(errorsList);
  }

  const values = errors.errors ? Object.values(errors.errors) : Object.values(errors);
  return getErrorFromArray(values);
};

export const generateExpireTime = () => {
  let date = new Date();
  date.setMinutes(date.getMinutes() + 600);
  date = new Date(date);
  return date.getTime();
};

export function formatDate(field?: Date | string | null) {
  if (field == null) return undefined;
  // The date picker expects the format yyyy-mm-dd according to the HTML specifications
  // and the swedish format happens to be just yyyy-mm-dd.
  return Intl.DateTimeFormat('sv-SE').format(field instanceof Date ? field : new Date(field));
}

export function parseAsUTC(dateStr: string | null | undefined): Date | null {
  if (!dateStr) return null;

  // Check if string already contains timezone info:
  // Z (UTC) or +hh:mm / -hh:mm
  const hasTimezone = /([zZ]|[+\-]\d{2}:\d{2})$/.test(dateStr);

  const normalized = hasTimezone ? dateStr : dateStr.replace(' ', 'T') + 'Z'; // ensure ISO format

  return new Date(normalized);
}

export function dateToIsoString(field?: Date | string | null) {
  if (field == null) {
    return undefined;
  }

  const parsed = field instanceof Date ? field : parseAsUTC(field);
  if (!parsed || Number.isNaN(parsed.getTime())) {
    return undefined;
  }

  const year = parsed.getUTCFullYear();
  const month = String(parsed.getUTCMonth() + 1).padStart(2, '0');
  const day = String(parsed.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function formatAsUTCDate(field?: Date | string | null) {
  if (field == null) return undefined;
  const d = field instanceof Date ? field : new Date(field);
  const utcDate = new Date(
    Date.UTC(
      d.getFullYear(), // Year
      d.getMonth(), // Month (0-11)
      d.getDate(), // Day of the month (1-31)
      d.getHours(), // Hours (0-23)
      d.getMinutes(), // Minutes (0-59)
      d.getSeconds(), // Seconds (0-59)
      d.getMilliseconds() // Milliseconds (0-999)
    )
  );
  return utcDate;
}

export function formatDateTime(field?: Date | string) {
  const options: {
    year: string;
    month: string;
    day: string;
    hour: string;
    minute: string;
    hour12: boolean;
  } = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  };
  if (field == null) return undefined;
  // The date picker expects the format yyyy-mm-dd according to the HTML specifications
  // and the swedish format happens to be just yyyy-mm-dd.
  return Intl.DateTimeFormat('sv-SE', options as any).format(field instanceof Date ? field : new Date(field));
}

// PCR/c-PCR "Valid until" date is an inclusive UTC calendar day; expiry happens at 00:00:00 UTC on the following day.
export const isPcrExpired = (validUntil: string | Date | undefined | null, nowEpoch: number = Date.now()): boolean => {
  if (!validUntil) return false;

  // The API may return a date-time without a timezone designator (e.g. "2026-09-01T00:00:00"),
  // which `new Date(...)` would otherwise parse as local time instead of UTC.
  const validUntilDate = validUntil instanceof Date ? validUntil : parseAsUTC(validUntil);
  if (!validUntilDate || Number.isNaN(validUntilDate.getTime())) return false;

  const expiryEpoch = Date.UTC(
    validUntilDate.getUTCFullYear(),
    validUntilDate.getUTCMonth(),
    validUntilDate.getUTCDate() + 1
  );

  return nowEpoch >= expiryEpoch;
};

export const isWithin24Hours = (checkedDate: Date | undefined) => {
  if (!checkedDate) return false;

  const now = new Date();
  const nowInUTC = new Date(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
    now.getUTCHours(),
    now.getUTCMinutes(),
    now.getUTCSeconds()
  );
  const msIn24Hours = 24 * 60 * 60 * 1000;
  const timeDifference = nowInUTC.getTime() - checkedDate.getTime();

  return timeDifference >= 0 && timeDifference <= msIn24Hours;
};

export function isValidImageFile(file: File, supportedExtensions: string[]): boolean {
  const extensionIndex = file.name.lastIndexOf('.');
  if (extensionIndex === -1) {
    return false;
  }
  const fileExtension = file.name.substring(extensionIndex);
  return supportedExtensions.includes(fileExtension);
}

export const isPositiveDecimal = (val: any) => {
  let str = String(val);
  str = str.trim();

  if (!str || str === 'null') {
    return true;
  }

  let n = Number(str);

  return n !== Infinity && String(n) === str && n >= 0;
};

export function toOptions(dictionaryValues: DictionaryValue[]): Option[] {
  return dictionaryValues?.length
    ? dictionaryValues.map((item) => ({
        label: item.value,
        value: item.key.toString(),
        code: item.code,
      }))
    : [];
}

export const getOptionsFromDictionary = (data: DictionaryValue[] | undefined, code: string) =>
  Array.isArray(data)
    ? data
        .filter((x) => x.dictionaryCode === code)
        .sort((x, y) => x.ord - y.ord)
        .map(
          (x: DictionaryValue) =>
            ({
              value: x.key.toString(),
              label: x.value,
              code: x.code,
            } as Option)
        )
    : [];

export const formatUtcDateWithOffset = (date: Date) => {
  const utcDate = new Date(date);

  // Get the timezone offset in minutes
  const offsetMinutes = utcDate.getTimezoneOffset();

  // Apply the offset to the UTC time
  const localDate = new Date(utcDate.getTime() - offsetMinutes * 60 * 1000);

  // Calculate the offset hours and minutes
  const absOffsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
  const absOffsetMinutes = Math.abs(offsetMinutes) % 60;

  // Format the offset as "+HH:mm" or "-HH:mm"
  const offsetSign = offsetMinutes > 0 ? '-' : '+';
  const formattedOffset = `${offsetSign}${String(absOffsetHours).padStart(2, '0')}:${String(absOffsetMinutes).padStart(
    2,
    '0'
  )}`;

  return localDate.toISOString().replace('Z', formattedOffset);
};

export const formatCompanyAddress = (
  companyAddress: VerifierCompanyAddressModel | null | undefined,
  defaultValue: string
) => {
  return companyAddress
    ? [companyAddress.country, companyAddress.city, companyAddress.zipCode, companyAddress.addressLine]
        .filter((value: string | null | undefined) => !!value && value.trim() !== '')
        .join(', ')
    : defaultValue;
};

export const isArraySingleEmptyValue = (data: any[]) => {
  return (
    data.length == 1 &&
    Object.entries(data[0])
      .filter(([key]) => key !== 'epdVersionId' && key !== 'id' && key !== 'ord')
      .every(([_, value]) => value === null || value === undefined || value === '')
  );
};

export const NormalizeSpaces = (value: string) => value.trim().replace(/\s+/g, ' ');
